import { z } from 'zod';
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');
// Points at a goal, or at one of its milestones, plus the plan item the task came from. When a
// task is created the client names only the goal or milestone; the server creates the plan item
// and so `planItemId` is only present on a task that already exists.
const taskGoalSchema = z.object({
    goalRefinementId: z.string(),
    milestoneId: z.string().optional(),
    planItemId: z.string().optional(),
});
export const taskPriority = Object.freeze({
    LOW: {
        value: 1,
        label: 'Low',
    },
    MEDIUM: {
        value: 2,
        label: 'Medium',
    },
    HIGH: {
        value: 3,
        label: 'High',
    },
    URGENT: {
        value: 4,
        label: 'Urgent',
    },
});
export const taskFilters = Object.freeze({
    ALL: 'all',
    TODAY: 'today',
    UPCOMING: 'upcoming',
    COMPLETED: 'completed',
    UNCOMPLETED: 'uncompleted',
    OVERDUE: 'overdue',
    PROJECTS: 'projects',
    UNSCHEDULED: 'unscheduled',
});
const recurrenceSchema = z.object({
    rrule: z.string().optional().nullable(),
    disabled: z.boolean(),
    exclusions: z.array(daySchema).optional(),
    lastOccurrence: daySchema.optional().nullable(),
});
const taskCreateBaseSchema = z.object({
    title: z
        .string()
        .min(1, 'Task title is required')
        .max(100, 'Task title must be 100 characters or less')
        .trim(),
    description: z
        .string()
        .max(500, 'Task description must be 500 characters or less')
        .trim()
        .optional(),
    projectId: z.string().optional(),
    // Null clears it on an update
    when: daySchema.optional().nullable(),
    // Optional, as is `when`. Null clears it on an update.
    dueDate: daySchema.optional().nullable(),
    recurrence: recurrenceSchema,
    estimatedDuration: z.number().min(1).optional(),
    parentId: z.string().optional(),
    // 4 priorities: Low, Medium, High, Urgent (see taskPriority). "Immediate" (5) was removed.
    priority: z.number().min(1).max(4),
    labels: z.array(z.string()).optional(),
    goal: taskGoalSchema.optional(),
    isCommitted: z.boolean().default(false),
});
export const taskCreateSchema = taskCreateBaseSchema
    .refine((data) => {
    // If recurrence is enabled, when field must be set
    if (!data.recurrence.disabled && data.recurrence.rrule) {
        return !!data.when;
    }
    return true;
}, {
    message: 'Start date is required when recurrence is set',
    path: ['when'],
})
    // Both dates are optional; with both, the task cannot be due before it starts
    .refine((data) => !data.when || !data.dueDate || data.dueDate >= data.when, {
    message: 'Due date cannot be before the start date',
    path: ['dueDate'],
});
export const taskSchema = taskCreateBaseSchema.extend({
    id: z.string(),
    // Tasks created from goal plan items skip the create form's rules: their titles can run past 100
    // characters, and they were once stored with no recurrence and priority 0 ("none"). Accept what
    // the server stores rather than fail the whole task list on one task.
    title: z.string().min(1).trim(),
    recurrence: recurrenceSchema.default({ disabled: true }),
    priority: z.number().min(0).max(4),
    actualDuration: z.number().min(0).optional(),
    // Nullable so an update can explicitly clear the scheduled date.
    when: daySchema.optional().nullable(),
    // Null clears it on an update; left out, the server keeps it unless the task now starts after it
    dueDate: daySchema.optional().nullable(),
    // This task's place in its recurring series, 1 for the first
    occurrenceNumber: z.number().int().min(1).optional(),
    // Plan item ids of the prerequisites a goal plan task still waits on; set by the server
    blockedBy: z.array(z.string()).optional(),
    completedDate: daySchema.optional().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
